// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stoneage/App/Components/Notify/Notify";
import ProductQuantity from "@stoneage/App/Components/Qty/ProductQuantity";
import { CustomAppState, Point } from "@stoneage/App/Store/Reducers";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaRfqConfigureButtonClickEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Button, { ButtonIconPresentationProps, ButtonPresentationProps } from "@stoneage/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import { ProductAvailabilityStyles } from "@stoneage/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@stoneage/Overrides/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@stoneage/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@stoneage/Overrides/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@stoneage/Overrides/Components/ProductPrice";
import { ProductPriceSavingsMessageStyles } from "@stoneage/Overrides/Components/ProductPriceSavingsMessage";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
// eslint-disable-next-line spire/fenced-imports
import { hideAll as hideAllTippyInstances } from "tippy.js";

interface OwnProps {
    product?: ProductModel;
    productInfo?: ProductInfo;
    point?: Point;
    mainProductNumber: string;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    contactForQuoteWidgetFields: getWidgetsByPageId(state, getCurrentPage(state).id).find((widget: WidgetProps) => {
        return widget.type === "ProductDetails/ContactUsForQuoteButton";
    })?.fields,
    isAuthenticated:
        (state.context.session.isAuthenticated || state.context.session.rememberMe) && !state.context.session.isGuest,
    customerNumber: getBillToCustomerNumber(state),
    impersonation: trackAsImpersonation(state),
    bomConfigureOrRequestQuoteClickEvent: (state as CustomAppState).xGoogleAnalytics
        .bomConfigureOrRequestQuoteClickEvent,
    checkedProducts: state.xProductBomAction.checkedProducts,
    productTempQuantity:
        state.xProductBom[ownProps?.mainProductNumber]?.productsTempQuantities?.[
            ownProps?.productInfo?.productId || ""
        ] || 0,
    sessionCurrency: state.context.session.currency,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface PointDataStyles {
    wrapper?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    descriptionWrapper?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    partNumberStyle?: SmallHeadingAndTextStyles;
    productPrice?: ProductPriceStyles;
    notify?: TypographyProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    imageAndDescription?: InjectableCss;
    leftPart?: InjectableCss;
    rightPart?: InjectableCss;
    buttonWrapper?: InjectableCss;
    button?: ButtonPresentationProps;
    productPartNumbers?: ProductPartNumbersStyles;
    price?: ProductPriceStyles;
    priceSavingExtendedStyles?: ProductPriceSavingsMessageStyles;
    description?: TypographyPresentationProps;
    partNumber?: SmallHeadingAndTextStyles;
    priceAndInventoryWrapper?: InjectableCss;
    availabilityStyles?: ProductAvailabilityStyles;
    priceTextStyle?: TypographyPresentationProps;
}

export const pointDataStyles: PointDataStyles = {};

const PointData: React.FC<Props> = ({
    product,
    productInfo,
    contactForQuoteWidgetFields,
    point,
    isAuthenticated,
    impersonation,
    customerNumber,
    bomConfigureOrRequestQuoteClickEvent,
    checkedProducts,
    productTempQuantity,
    mainProductNumber,
    sessionCurrency,
}) => {
    const dispatch = useDispatch();
    const styles = pointDataStyles;
    const toasterContext = useContext(ToasterContext);

    const [qty, setQty] = useState(productInfo?.qtyOrdered || 1);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo?.unitOfMeasure || "");

    useEffect(() => {
        productInfo?.unitOfMeasure && setUnitOfMeasure(productInfo.unitOfMeasure);
    }, [productInfo?.unitOfMeasure]);

    useEffect(() => {
        if (productInfo && productTempQuantity) {
            updateQuantity(productTempQuantity);
        }
    }, [productTempQuantity]);

    if ((!product || !productInfo) && !point) {
        return (
            <Notify messageType="warning" {...styles.notify}>
                {translate("Product Not Found")}
            </Notify>
        );
    }
    if (!product || !productInfo) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.imageAndDescription}>
                    <StyledWrapper {...styles.leftPart}>
                        <LazyImage {...styles.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightPart}>
                        <Typography {...styles.description}>{point?.description}</Typography>
                        <SmallHeadingAndText
                            extendedStyles={styles.partNumber}
                            heading={translate("Part #")}
                            text={point?.productNumber || ""}
                        ></SmallHeadingAndText>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const productContext = {
        product: {
            ...product,
        },
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    } as ProductContextModel;

    const configureProductHandler = () => {
        bomConfigureOrRequestQuoteClickEvent &&
            trackGaRfqConfigureButtonClickEvent(
                product.productNumber || point?.productNumber || "",
                translate("Configure Product"),
                impersonation,
                customerNumber,
            );
        document.getElementById(`${product.id}_click`)?.click();
        const parentPosition = document.getElementById(product.id);
        if (parentPosition) {
            parentPosition.scrollIntoView({
                behavior: "smooth",
                block: "end",
                inline: "nearest",
            });
            parentPosition.scrollIntoView({ behavior: "smooth", block: "start" });

            const elementPosition = parentPosition.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
                top: elementPosition - 100, // header height
                behavior: "smooth",
            });
        }
        hideAllTippyInstances();
    };

    const addToQuoteHandler = (_: React.SyntheticEvent) => {
        dispatch({
            type: "xProductBomAction/selectProduct",
            productInfo: productContext.productInfo,
            product: productContext.product,
        });
        dispatch({
            type: "xProductBom/UpdateQuantity",
            productId: productInfo.productId,
            quantity: qty,
            key: mainProductNumber,
        } as any);
        hideAllTippyInstances();
        toasterContext.addToast({
            body: translate("Quantity Updated Successfully"),
            messageType: "success",
        });
    };

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.imageAndDescription}>
                    <StyledWrapper {...styles.leftPart}>
                        <LazyImage src={product?.smallImagePath} altText={product.imageAltText} {...styles.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightPart}>
                        <ProductDescription
                            extendedStyles={styles.productDescription}
                            product={productContext}
                            lines={3}
                            lineHeight={19}
                        />
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showManufacturerItem={false}
                            extendedStyles={styles.productPartNumbers}
                        />
                        <ProductPrice
                            product={productContext}
                            showSavings={true}
                            showSavingsAmount={true}
                            showSavingsPercent={true}
                            showMsrpAndSaving={true}
                            showInvalidPriceMessage={false}
                            showLabel={false}
                            extendedStyles={styles.price}
                            priceSavingExtendedStyles={styles.priceSavingExtendedStyles}
                        />
                        <StyledWrapper {...styles.priceAndInventoryWrapper}>
                            {isAuthenticated &&
                            !product.isVariantParent &&
                            productContext.productInfo.pricing?.unitNetPrice === 0 ? (
                                <Typography translate="no" {...styles.priceTextStyle}>
                                    {sessionCurrency?.currencySymbol || sessionCurrency?.currencyCode}–.––
                                </Typography>
                            ) : (
                                <ProductPrice
                                    product={productContext}
                                    showSavings={false}
                                    showSavingsAmount={false}
                                    showSavingsPercent={false}
                                    showInvalidPriceMessage={true}
                                    showLabel={false}
                                    extendedStyles={styles.price}
                                    priceSavingExtendedStyles={styles.priceSavingExtendedStyles}
                                />
                            )}
                            {isAuthenticated && !product.isVariantParent && (
                                <StyledWrapper>
                                    <ProductContextAvailability
                                        isProductDetailsPage={false}
                                        extendedStyles={styles.availabilityStyles}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        {!product.isVariantParent && <ProductQuantity value={qty} showLabel={false} allowCantBuy />}
                    </StyledWrapper>
                </StyledWrapper>
                {product && productInfo && (
                    <StyledWrapper {...styles.buttonWrapper}>
                        {product.isVariantParent ? (
                            <Button onClick={configureProductHandler} {...styles.button}>
                                {translate("Configure Product")}
                            </Button>
                        ) : (
                            <Button onClick={addToQuoteHandler} {...styles.button}>
                                {translate("Add to quote")}
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps)(PointData);
